import { SideBarFull } from "../components/SideBarFull"
import { useQuery } from "@tanstack/react-query"
import { getAllUnreadThreads } from "@/features/conversations/api"
import { TThread } from "@/features/conversations/types"
import { TMenuItems, TSideBarItem } from "../types/SideBarItem"
import {
  Sheet,
  SheetClose,
  SheetContent,
} from "@/core/components/dialogs/Sheet"
import { SideBarCompact } from "../components/SideBarCompact"
import { useRecoilValue } from "recoil"
import { meAtom } from "@/atoms/users"
import { isSubspace, selectedSpaceAtom } from "@/atoms/spaces"
import { useTranslation } from "next-i18next"
import { Illustration } from "@/core/components/Illustration"
import { Icon } from "@/core/components/icon/Icon"
import { Button } from "@/core/components/Button"
import { createContext, useContext, useEffect, useMemo, useState } from "react"
import { useRouter } from "next/router"
import { CustomerType } from "@/modules/space/constants/customerType"
import { useSpaceUserContext } from "@/modules/users/hooks/useSpaceUserContext"
import { customerTypeNavVisibility } from "../utils/customerTypeNav"

type NavigationContextType = {
  open: boolean
  setOpen: (open: boolean) => void
}

export const NavigationContext = createContext<NavigationContextType | null>(
  null,
)
export const NavigationProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [open, setOpen] = useState(false)
  const value = useMemo<NavigationContextType>(
    () => ({ open, setOpen }),
    [open, setOpen],
  )
  return (
    <NavigationContext.Provider value={value}>
      <SideBar />
      {children}
    </NavigationContext.Provider>
  )
}

const convertMenuItemsToSections = (
  menuItems: TMenuItems,
  filterLocked = false,
): { sections: { items: TSideBarItem[] }[] } => {
  return {
    sections: Object.entries(menuItems).map(([index, section]) => {
      return {
        items: Object.entries(section)
          .map(([index, item]) => {
            if (filterLocked && item.locked) return null
            if (item.subItem) {
              return {
                ...item,
                subItem: Object.entries(item.subItem).map(
                  ([index, subItem]) => {
                    return {
                      ...subItem,
                      subItem: null,
                    }
                  },
                ),
              }
            }
            return item as TSideBarItem
          })
          .filter((e) => e !== null) as TSideBarItem[],
      }
    }),
  }
}

export const SideBar = () => {
  const { t } = useTranslation("common")
  const router = useRouter()
  const { open, setOpen } = useContext(NavigationContext)
  const { data: unreads, isLoading: isLoadignUnread } = useQuery<TThread[]>({
    queryKey: ["threads/all-unreads"],
    queryFn: () => getAllUnreadThreads({ limit: 100 }),
  })

  const user = useRecoilValue(meAtom)
  const isSupplier = useRecoilValue(isSubspace)
  const selectedSpace = useRecoilValue(selectedSpaceAtom)
  const { data: spaceUserContext } = useSpaceUserContext(selectedSpace?.uid)

  useEffect(() => {
    router.events.on("routeChangeStart", () => setOpen(false))
    return () => {
      router.events.off("routeChangeStart", () => setOpen(false))
    }
  }, [router.events])

  if (!user || !selectedSpace) return null

  // block if space is organization and customer type is not B2B and user is not employee, kam or contact
  const blockAccess =
    selectedSpace.isOrganization &&
    selectedSpace.customerType !== CustomerType.B2B &&
    !user?.is_employee &&
    !user?.has_kam_access &&
    !user?.is_contact

  const hasKAMAccess: boolean = Boolean(user.has_kam_access)
  const isEmployee = Boolean(user.is_employee)
  const isHiringManager: boolean = Boolean(spaceUserContext?.isHiringManager)

  const isCustomer = !isSupplier
  const isB2B = selectedSpace.customerType === CustomerType.B2B
  const customerTypeNav = customerTypeNavVisibility(selectedSpace.customerType)
  const hideExternalProposals =
    !isEmployee ||
    selectedSpace.customerType === CustomerType.B2B ||
    selectedSpace.customerType === CustomerType.RFQSender

  const hasAdminAccess: boolean =
    user._space?.uid === "a418e91f-452a-4a0a-b905-447e2187758f" &&
    user.email.endsWith("@flextribe.io")

  const menuItems: TMenuItems = {
    home: {
      home: {
        label: "home",
        icon: "home",
        href: "/home",
        imageSrc: null,
        subItem: null,
        locked: !selectedSpace.settings.enable_approval_dashboard,
      },
      messages: {
        label: "messages",
        icon: "chat_bubble",
        href: "/inbox",
        imageSrc: null,
        subItem: null,
        locked: selectedSpace.settings.disabled_messages || blockAccess,
      },
    },

    tasks: {
      opportunities: {
        label: "opportunities",
        icon: "radar",
        href: "/leads",
        imageSrc: null,
        subItem: null,
        locked:
          selectedSpace.settings.disabled_leads || !isCustomer || !isEmployee,
      },
      requests: isCustomer
        ? hideExternalProposals
          ? {
              label:
                selectedSpace.customerType === CustomerType.B2B
                  ? "sourcing_requests"
                  : "requests",
              icon: "inbox_text",
              href: "/shared",
              //update subpaths when we can differentiate between proposals and assignments
              subPaths: [],
              imageSrc: null,
              subItem: null,
              locked:
                selectedSpace.settings.disabled_internal_proposals ||
                !isCustomer,
            }
          : {
              label: "requests",
              icon: "inbox_text",
              href: null,
              imageSrc: null,
              locked: false,
              //update subpaths when we can differentiate between proposals and assignments
              subPaths: [],
              subItem: {
                internal_requests: {
                  label: "own_requests",
                  icon: "inbox_text",
                  href: "/shared",
                  imageSrc: null,
                  subItem: null,
                  locked:
                    selectedSpace.settings.disabled_internal_proposals ||
                    hasKAMAccess
                      ? !hasKAMAccess
                      : !isCustomer,
                },
                external_requests: {
                  label: "shared_requests",
                  icon: "inbox_text",
                  href: "/supplier/opportunities",
                  imageSrc: null,
                  subItem: null,
                  locked:
                    isCustomer &&
                    selectedSpace.settings.disabled_external_proposals,
                },
              },
            }
        : {
            label: "requests",
            icon: "inbox_text",
            href: null,
            imageSrc: null,
            locked: false,
            //update subpaths when we can differentiate between proposals and assignments
            subPaths: [],
            subItem: {
              ...(hasKAMAccess
                ? {
                    internal_requests: {
                      label: "own_requests",
                      icon: "inbox_text",
                      href: "/shared",
                      imageSrc: null,
                      subItem: null,
                      locked: false,
                    },
                  }
                : null),
              external_requests: {
                label: "shared_requests",
                icon: "inbox_text",
                href: "/supplier/opportunities",
                imageSrc: null,
                subItem: null,
                locked: false,
              },
            },
          },
      ...(customerTypeNav.assignments && {
        ongoing_tasks: {
          label: "assignments",
          icon: "work",
          href: "/assignments",
          imageSrc: null,
          subItem: null,
          locked: blockAccess,
        },
      }),
    },
    resources: {
      ...(customerTypeNav.resources
        ? {
            resources: {
              label: "resources",
              icon: "group" as const,
              href: "/resources",
              imageSrc: null,
              subItem: null,
              locked:
                isSupplier ||
                selectedSpace.settings.disabled_resources ||
                !isEmployee,
            },
          }
        : {}),
      ...(customerTypeNav.consultants ||
      !selectedSpace.settings.disabled_consultants
        ? {
            consultants: {
              label: "consultants",
              icon: "groups" as const,
              href: "/consultants",
              imageSrc: null,
              subItem: null,
              locked: !customerTypeNav.consultants,
            },
          }
        : {}),
      companies: {
        label: "customers_and_partners",
        icon: "corporate_fare",
        href: "/companies",
        imageSrc: null,
        subItem: null,
        locked:
          (isCustomer && selectedSpace.settings.disabled_companies) ||
          !isEmployee,
      },
      customer_agreements: {
        label:
          selectedSpace.customerType === CustomerType.B2B
            ? "framework_agreements"
            : "customer_agreements",
        icon: "contract",
        href: "/agreements",
        subPaths: ["/agreement/"],
        imageSrc: null,
        subItem: null,
        locked:
          selectedSpace.settings.disabled_agreements ||
          !isCustomer ||
          !isEmployee,
      },
      contracts: {
        label: "contracts",
        icon: "receipt",
        href: "/contract/list",
        imageSrc: null,
        subItem: null,
        locked:
          (!isEmployee && isCustomer) ||
          (isCustomer && selectedSpace.settings.disabled_contracts),
      },
    },
    analytics: {
      // Show a single "Analytics" link, picked by customer type — B2B spaces
      // get the B2E analytics page, everyone else the normal one. Only the
      // applicable link is rendered (the other is omitted, not just locked).

      b2e_analytics: {
        label: "analytics",
        icon: "monitoring",
        href: "/b2e-analytics",
        imageSrc: null,
        subItem: null,
        locked:
          isSupplier ||
          selectedSpace.settings.disabled_b2b_analytics ||
          !isEmployee,
      },

      analytics: {
        label: "analytics",
        icon: "monitoring",
        href: "/analytics",
        imageSrc: null,
        subItem: null,
        locked:
          isSupplier ||
          selectedSpace.settings.disabled_analytics ||
          !isEmployee,
      },
      timeline: {
        label: "timeline",
        icon: "view_timeline",
        href: "/timeline",
        imageSrc: null,
        subItem: null,
        locked:
          isSupplier || selectedSpace.settings.disabled_timeline || !isEmployee,
      },
      time_sheet: {
        label: "time_sheet",
        icon: "overview",
        href: "/time_registration",
        imageSrc: null,
        subItem: null,
        locked:
          isSupplier ||
          selectedSpace.settings.disabled_time_sheet ||
          !isEmployee,
      },
    },
    settings: hasAdminAccess
      ? {
          admin_dashboard: {
            label: "admin_dashboard",
            icon: "shield_person",
            href: "/admin/dashboard",
            imageSrc: null,
            subItem: null,
            locked: blockAccess,
          },
          profile_settings: {
            label: "profile",
            icon: "person",
            href: "/settings/profile",
            imageSrc: user.email,
            subItem: null,
            locked: blockAccess, // Master piece of code right there
          },
        }
      : {
          profile_settings: {
            label: "profile",
            icon: "person",
            href: "/settings/profile",
            imageSrc: user.email,
            subItem: null,
            locked: blockAccess, // Master piece of code right there
          },
        },
  }

  const menuItemsSections = convertMenuItemsToSections(
    menuItems,
    selectedSpace.settings.hideSupplierSalesNav,
  )

  return (
    <nav className="top-0 z-40 h-screen w-fit">
      <div className="h-full w-fit 2xl:hidden">
        <Sheet open={open} onOpenChange={(isOpen) => setOpen(isOpen)}>
          {/* <SideBarCompact t={t} sections={menuItemsSections.sections} /> */}
          <SheetContent
            aria-describedby=""
            className="bg-container-base sm:w-sidebar flex h-full !w-[20rem] max-w-[32rem] flex-col gap-4 p-3 sm:rounded-bl-none sm:rounded-br-xl sm:rounded-tl-none sm:rounded-tr-xl"
            side="left"
          >
            <div className="flex" tabIndex={0}>
              <SheetClose asChild>
                <Button
                  variant="text"
                  size="icon"
                  className="m-3 ml-2.5 p-2"
                  text={t("close")}
                >
                  <Icon
                    icon="menu_open"
                    className="text-on-surface-variant"
                    size="1.5rem"
                  />
                </Button>
              </SheetClose>
            </div>
            <SideBarFull sections={menuItemsSections.sections} />
          </SheetContent>
        </Sheet>
      </div>
      <div className="bg-container-base w-sidebar hidden h-full flex-col p-3 2xl:flex">
        <div className="flex flex-col px-3 py-6">
          <Illustration
            name="flextribe-x"
            alt="Logo symbol"
            height={48}
            width={48}
          />
        </div>
        <SideBarFull sections={menuItemsSections.sections} />
      </div>
    </nav>
  )
}
